import { useState } from 'react';
import { useApi } from '../api/ApiProvider';
import { useMe } from '../hooks/useMe';
import { Button } from './ui/Button';
import { Dialog } from './ui/Dialog';
import { ErrorBanner } from './ui/ErrorBanner';

export interface ArchiveJobButtonProps {
  siteId: string;
  jobReference: string;
  archived: boolean;
  /** The caller's effective actions on the job folder; `archive` is required. */
  canArchive: boolean;
  onDone?: () => void;
}

/** D7: archive is a status, not a delete. AR (or Records.Admin) flips a job folder with one click. */
export function ArchiveJobButton({ siteId, jobReference, archived, canArchive, onDone }: ArchiveJobButtonProps) {
  const api = useApi();
  const { hasRole } = useMe();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  if (!canArchive || !hasRole('AR.Specialist', 'Records.Admin')) return null;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      if (archived) await api.jobs.reopen(siteId);
      else await api.jobs.archive(siteId);
      setOpen(false);
      onDone?.();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="secondary" icon="archive" onClick={() => setOpen(true)}>
        {archived ? 'Reopen job' : 'Archive job'}
      </Button>
      <Dialog
        open={open}
        title={archived ? 'Reopen job folder' : 'Archive job folder'}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button busy={busy} onClick={() => void confirm()}>
              {archived ? 'Reopen' : 'Archive'}
            </Button>
          </>
        }
      >
        <p>
          {archived
            ? `Move “${jobReference}” back under Jobs so documents can be added again.`
            : `Move “${jobReference}” under Archive. Everything stays readable; nothing is deleted and no new uploads are accepted until it is reopened.`}
        </p>
        {error ? (
          <div className="mt-3">
            <ErrorBanner error={error} />
          </div>
        ) : null}
      </Dialog>
    </>
  );
}
